import { GameConfig, RouteName, visibleDesignHeight } from "../config/GameConfig";

type MotionParticle = {
    view: Laya.Box;
    baseX: number;
    baseY: number;
    speed: number;
    phase: number;
    drift: number;
};

/** Lightweight vector motion shared by every page of the red-packet game. */
export class FestiveMotion extends Laya.Box {
    private readonly particles: MotionParticle[] = [];
    private tickCount = 0;

    constructor(route: RouteName) {
        super();
        this.name = "festive_motion";
        this.width = GameConfig.DESIGN_WIDTH;
        this.height = Math.max(300, visibleDesignHeight() - 112);
        this.mouseEnabled = false;
        this.alpha = route === "quiz" ? 0.72 : 0.88;

        const count = route === "home" ? 7 : route === "quiz" ? 4 : 5;
        for (let index = 0; index < count; index++) {
            const kind = index % 3 === 0 ? "packet" : index % 3 === 1 ? "coin" : "spark";
            const view = kind === "packet" ? this.packet() : kind === "coin" ? this.coin() : this.spark();
            const edgeOnly = route === "quiz" || route === "auth";
            const baseX = edgeOnly
                ? (index % 2 === 0 ? 18 + index * 7 : GameConfig.DESIGN_WIDTH - 56 - index * 5)
                : 30 + ((index * 127 + route.length * 29) % 670);
            const baseY = 130 + ((index * 239 + route.length * 41) % Math.max(260, this.height - 240));
            view.pos(baseX, baseY);
            view.alpha = kind === "spark" ? 0.65 : 0.42;
            this.addChild(view);
            this.particles.push({
                view,
                baseX,
                baseY,
                speed: 0.018 + index * 0.0025,
                phase: index * 1.37,
                drift: 7 + (index % 3) * 4,
            });
        }

        this.addBalanceGlint();
        Laya.timer.frameLoop(2, this, this.updateMotion);
    }

    destroy(destroyChild = true): void {
        Laya.timer.clearAll(this);
        Laya.Tween.clearAll(this);
        this.particles.forEach(item => Laya.Tween.clearAll(item.view));
        super.destroy(destroyChild);
    }

    static celebrate(target: Laya.Sprite): void {
        const layer = new Laya.Box();
        layer.name = "red_packet_confetti";
        layer.width = GameConfig.DESIGN_WIDTH;
        layer.height = GameConfig.DESIGN_HEIGHT;
        layer.mouseEnabled = false;
        target.addChild(layer);

        for (let index = 0; index < 18; index++) {
            const chip = new Laya.Box();
            const isPacket = index % 3 === 0;
            chip.width = isPacket ? 20 : 12;
            chip.height = isPacket ? 25 : 12;
            if (isPacket) {
                chip.graphics.drawRoundRect(0, 0, 20, 25, 4, 4, 4, 4, "#F04432", "#FFD45C", 2);
                chip.graphics.drawCircle(10, 12, 4, "#FFD45C");
            } else {
                chip.graphics.drawRect(0, 0, 12, 12, index % 2 ? "#FFD447" : "#62D05A");
            }
            chip.pos(40 + ((index * 91) % 660), -30 - (index % 5) * 36);
            chip.rotation = index * 23;
            chip.mouseEnabled = false;
            layer.addChild(chip);
            Laya.Tween.to(
                chip,
                { y: 760 + (index % 4) * 80, x: chip.x + (index % 2 ? 45 : -35), rotation: chip.rotation + 300, alpha: 0.15 },
                1350 + (index % 5) * 150,
                Laya.Ease.quadIn,
            );
        }
        Laya.timer.once(2300, layer, () => layer.destroy(true));
    }

    private updateMotion(): void {
        this.tickCount += 2;
        for (const item of this.particles) {
            const angle = this.tickCount * item.speed + item.phase;
            item.view.x = item.baseX + Math.sin(angle * 0.72) * item.drift;
            item.view.y = item.baseY + Math.sin(angle) * (item.drift + 3);
            item.view.rotation = Math.sin(angle * 0.55) * 7;
            item.view.alpha = 0.34 + (Math.sin(angle * 1.4) + 1) * 0.12;
        }
    }

    private packet(): Laya.Box {
        const box = new Laya.Box();
        box.width = 38;
        box.height = 48;
        box.graphics.drawRoundRect(1, 2, 36, 44, 7, 7, 7, 7, "#E84030", "#FFD36C", 3);
        box.graphics.drawPoly(2, 5, [0, 0, 34, 0, 17, 17], "#F75A3E", "#FFD36C", 2);
        box.graphics.drawCircle(19, 24, 7, "#FFC83E", "#9B431F", 2);
        return box;
    }

    private coin(): Laya.Box {
        const box = new Laya.Box();
        box.width = 34;
        box.height = 34;
        box.graphics.drawCircle(17, 17, 15, "#F6B82E", "#FFF09B", 3);
        box.graphics.drawCircle(17, 17, 10, "#F9CE4D", "#B66A1F", 2);
        const mark = new Laya.Label("¥");
        mark.width = 34;
        mark.height = 32;
        mark.fontSize = 20;
        mark.bold = true;
        mark.color = "#8B3B18";
        mark.align = "center";
        mark.valign = "middle";
        mark.mouseEnabled = false;
        box.addChild(mark);
        return box;
    }

    private spark(): Laya.Box {
        const box = new Laya.Box();
        box.width = 28;
        box.height = 28;
        box.graphics.drawPoly(14, 14, [0, -13, 4, -4, 13, 0, 4, 4, 0, 13, -4, 4, -13, 0, -4, -4], "#FFE77A");
        return box;
    }

    private addBalanceGlint(): void {
        const glint = this.spark();
        glint.name = "balance_glint";
        glint.pos(676, 36);
        glint.alpha = 0.25;
        glint.scale(0.55, 0.55);
        this.addChild(glint);
        const pulse = (large: boolean) => {
            if (glint.destroyed) return;
            Laya.Tween.to(
                glint,
                { alpha: large ? 0.95 : 0.2, scaleX: large ? 1.05 : 0.5, scaleY: large ? 1.05 : 0.5, rotation: glint.rotation + 45 },
                large ? 520 : 800,
                Laya.Ease.sineInOut,
                Laya.Handler.create(glint, () => pulse(!large)),
            );
        };
        pulse(true);
    }
}
